import { Reveal } from "./Reveal";
import { certifications, type Certification } from "@/data/certifications";
import { CertificationLogo } from "./CertificationLogo";

interface Props {
  light?: boolean;
  variant?: "full" | "compact";
}

export function CertificationsStrip({ light = false, variant = "full" }: Props) {
  const items: Certification[] = (
    variant === "compact"
      ? certifications.filter((c) => c.status !== "in-progress").slice(0, 4)
      : certifications
  ).slice().sort((a, b) => a.priority - b.priority);

  if (variant === "compact") {
    return (
      <section className={light ? "py-8 bg-brand-cream" : "py-8 bg-brand-cream-dark/40"}>
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-10">
            <span className="font-italic-serif italic text-brand-warm-gray text-sm tracking-wide whitespace-nowrap">
              Le nostre garanzie:
            </span>
            <div className="grid grid-cols-3 md:flex md:flex-nowrap items-center justify-items-center md:justify-center gap-x-6 gap-y-4 md:gap-x-10">
              {items.map((c) => (
                <LogoLink key={c.id} cert={c} cellHeight="h-10 md:h-12" />
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={light ? "py-14 bg-brand-cream" : "py-14 bg-brand-cream-dark/40"}>
      <div className="container mx-auto px-4">
        <Reveal>
          <p className="text-center font-italic-serif italic text-brand-warm-gray mb-10 tracking-wide text-lg">
            Riconoscimenti e certificazioni
          </p>
        </Reveal>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-6 gap-y-10 items-center justify-items-center max-w-6xl mx-auto">
          {items.map((c, i) => (
            <Reveal key={c.id} delay={i * 0.05} className="w-full flex justify-center">
              <LogoLink cert={c} cellHeight="h-16 md:h-20" />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function LogoLink({ cert, cellHeight }: { cert: Certification; cellHeight: string }) {
  const inner = (
    <div className={`relative inline-flex items-center justify-center grayscale opacity-70 hover:grayscale-0 hover:opacity-100 hover:scale-105 transition-all duration-300 ${cellHeight} w-full max-w-[160px]`}>
      <CertificationLogo cert={cert} className="max-h-full max-w-full w-auto h-auto" />
      {cert.status === "in-progress" && (
        <span className="absolute -top-2 -right-2 bg-brand-gold text-brand-charcoal text-[9px] tracking-widest uppercase px-1.5 py-0.5 rounded-sm font-semibold">
          In corso
        </span>
      )}
    </div>
  );
  if (cert.externalLink) {
    return (
      <a
        href={cert.externalLink}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`${cert.name} — sito ufficiale`}
        className="inline-flex w-full justify-center"
      >
        {inner}
      </a>
    );
  }
  return inner;
}
